import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import React, { ReactNode, useEffect } from "react";

type DataDogContextType = {
  sendEventToDataDog: (
    eventName: string,
    attributes?: Record<string, any>
  ) => Promise<void>;
};

const DataDogContext = React.createContext<DataDogContextType>({
  sendEventToDataDog: async () => {},
});

type DataDogProviderProps = {
  children: ReactNode | ReactNode[];
};

function DataDogProvider(props: DataDogProviderProps) {
  const sendEventToDataDog = async (
    eventName: string,
    attributes?: Record<string, any>
  ) => {
    try {
      datadogRum.addAction(eventName, attributes);
    } catch (e: any) {
      console.log(e);
    }
  };

  const dataDogInit = () => {
    datadogRum.init({
      applicationId: process.env.REACT_APP_DATADOG_APPLICATION_ID ?? "",
      clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN ?? "",
      // `site` refers to the Datadog site parameter of your organization
      // see https://docs.datadoghq.com/getting_started/site/
      site: "datadoghq.com",
      service: process.env.REACT_APP_DATADOG_SERVICE_NAME,
      env: process.env.REACT_APP_DATADOG_ENV,
      // Specify a version number to identify the deployed version of your application in Datadog
      // version: '1.0.0',
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
    });
    datadogRum.startSessionReplayRecording();
    datadogLogs.init({
      clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN ?? "",
      site: "datadoghq.com",
      service: process.env.REACT_APP_DATADOG_SERVICE_NAME,
      forwardErrorsToLogs: true,
      env: process.env.REACT_APP_DATADOG_ENV,
      sessionSampleRate: 100,
    });
  };

  useEffect(() => {
    if (
      process.env.REACT_APP_DATADOG_APPLICATION_ID &&
      process.env.REACT_APP_DATADOG_CLIENT_TOKEN &&
      process.env.REACT_APP_DATADOG_ENV &&
      process.env.REACT_APP_DATADOG_SERVICE_NAME
    ) {
      dataDogInit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    process.env.REACT_APP_DATADOG_APPLICATION_ID,
    process.env.REACT_APP_DATADOG_CLIENT_TOKEN,
    process.env.REACT_APP_DATADOG_ENV,
    process.env.REACT_APP_DATADOG_SERVICE_NAME,
  ]);

  return <DataDogContext.Provider value={{ sendEventToDataDog }} {...props} />;
}

function useDataDog() {
  const context = React.useContext(DataDogContext);

  if (context === undefined) {
    throw new Error("useCoach must be used within a CoachProvider");
  }
  return context;
}

export { DataDogProvider, useDataDog };
